/**
 * Licensed Materials - Property of IBM
 * IBM Watson Analytics (C) Copyright IBM Corp. 2017, 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'./FilterBaseController',
	'./HierarchicalFilterView',
	'./HierarchicalFilterModel',
	'underscore'
], function (BaseClass, FilterView, FilterModel, _) {
	'use strict';
	const ROW_LIMIT = 51;


	/**
	 * The controller holds the business logic for the hierarchical filter
	 */
	var HierarchicalFilterController = BaseClass.extend({

		init: function () {
			HierarchicalFilterController.inherited('init', this, arguments);
			this.id = _.uniqueId('HierarchicalFilterController');
			this.ready = Promise.resolve(null);
		},

		initialize: function() {
			this._initializeExtraData();
			return this._initializeModelAndView();
		},

		_initializeModelAndView: function() {
			this._createFilterModel();
			this._createFilterView();
			this.setLoading();

			this._resetModelFromPersistedValues()
				.then(this.onInitializationDone.bind(this));
			return Promise.resolve();
		},

		_initializeExtraData: function () {
			this._initializePageContext(/*isRange*/true);
		},

		_createFilterModel: function() {
			this.model = new FilterModel({ rowLimit:ROW_LIMIT });
			this.model.reset(); // initialize with empty array
		},
		_createFilterView: function() {
			this.viewConfig = _.extend(this.viewConfig, {
				model: this.model,
				cbOnClick: this.onClick.bind(this),
				cbOnClear: this.onClear.bind(this),
				cbOnDone: this.onDone.bind(this),
				cbOnApply: this.onApply.bind(this),
				cbOnClose: this.onClose.bind(this),
				cbOnSearch: this.onSearch.bind(this),
				cbOnDoneSearch: this.onDoneSearch.bind(this),
				cbOnExpandCollapse: this.onExpandCollapse.bind(this),
				cbOnGetChildrenLimit: this.onGetChildrenLimit.bind(this),
				cbOnLoadMore: this.onLoadMore.bind(this),
				cbOnInvert: this.onInvert.bind(this),
				cbGetValue: this.onGetValue.bind(this),
				cbGetNumberOfChildren: this.getNumberOfChildren.bind(this),
				cbListOnClick: this.onClick.bind(this),
				cbListGetValue: this.onListGetValue.bind(this),
				cbListOnInvert: this.onInvert.bind(this),
				cbListGetNumberOfChildren: this.listGetNumberOfChildren.bind(this)
			});
			this.view = new FilterView(this.viewConfig);
		},

		onGetChildrenLimit: function(itemId){
			return this.model.getChildrenLimit(itemId);
		},

		onLoadMore: function(containerId, offset){
			this.onExpandCollapse(containerId, null, offset);
		},

		onExpandCollapse: function(containerId, isExpanded, offset = 0){
			if(!this.model.childrenLoaded(containerId, offset)){
				return this._getChildren(containerId, offset)
					.then(children => this.model.loadChildren(containerId, children, offset))
					.catch(e => this.processError(e));
			}
			return Promise.resolve();
		},


		/**
		 * Invoked when the following value changes.
		 */
		onClick: function (aSelectedItem) {
			this.model.toggleSelectState(aSelectedItem.id);
		},
		onInvert: function(){
			this.model.setInvert(!this.model.getInvert());
		},
		onListGetValue: function(index){
			return this.model.listGetValue(index);
		},
		listGetNumberOfChildren: function(){
			return this.model.listGetNumberOfChildren();
		},
		onGetValue: function(parentId, index){
			return this.model.getValue(parentId, index);
		},
		getNumberOfChildren: function(itemId){
			return this.model.getNumberOfChildren(itemId);
		},
		onClear: function () {
			this.model.clearValues();
		},
		onDone: function(){
			// Update PageContext if needed
			if( this.isFilterActive) {
				this.pageContextApi.updateFilterContext(this.itemContext, {
					command: 'replace',
					values: this.model.getSelectedMembers(),
					exclude:this.model.getInvert()
				});
			}
			if (!this.view.isCompact) {
				this.view.remove();
			}
		},

		onSearch: function(str){
			if (str) {
				return new Promise((resolve, reject) => {
					// this gets called for each letter typed by the user...
					// we wait until we are not called for 500ms before actually doing the search to not overwhelm the server.
					clearTimeout(this.searchTimeout);
					this.searchTimeout = setTimeout(function () {
						// Tell the MUI component to render a 'searching' indicator
						this.model.set({ isSearching: true });
						this._getColumnData(str)
							.then(function (searchResults) {
								this.model.set({ isSearching: false }, { silent: true });
								this.model.setSearchResults(searchResults);
								resolve();
							}.bind(this))
							.catch(function (e) {
								this.model.set({ isSearching: false });
								this.processError(e);
								reject(e);
							}.bind(this));
					}.bind(this), 500);
				});
			} else {
				return this.onDoneSearch();
			}
		},

		/**
		 * Is called when 'Return to tree' is hit or search string is empty. Root and ascendants of all selected items get fetched,
		 * so we know where in the tree they need to go. Model get reset with the fetched values.
		 */
		onDoneSearch: function(){
			let selectedItems = this._mergeSelectedItemsWithSearchSelectedItems();
			this.model.reset({ silent: true });
			return Promise.all([
				//TODO: Added story 252249 to separate tree model and search model to prevent making extra query calls
				//no matter whether any items is selected in seardch result or not.
				this._getRootMembers(),
				this._getAscendants(selectedItems)
			]).then(function (results) {
				var roots = results[0];
				var ascendants = results[1];

				this.model.reset({ roots: roots, ascendants: ascendants, initialSelection: selectedItems });
				this.view.resetView();
			}.bind(this))
				.catch( function(e) {
					this.processError(e);
				}.bind(this));
		},

		/**
		 * Merges selected items comming back from search result along with selected items we picked before the search!
		 * There is a posibility that the selected items is duplicated in the merge,
		 * so removing duplicated items in '_selectedItemsFromSearch' array.
		 */
		_mergeSelectedItemsWithSearchSelectedItems: function() {
			let _selectedItemsFromSearch = this.model.getSelectedItemsFromSearch();
			_selectedItemsFromSearch = _selectedItemsFromSearch.concat(this.model.getSelectedMembers());
			_selectedItemsFromSearch = _selectedItemsFromSearch.filter((item, index, self) =>
				index === self.findIndex((t) => (
					t.u === item.u && t.d === item.d
				))
			);
			let _selectedItems = [];
			_.each(_selectedItemsFromSearch, function(item){
				_selectedItems.push(item.u);
			});
			return _selectedItems;
		},

		onOpen: function () {
			this.setLoading();
			if (this._ensureFilterViewExists()) {
				// New view or model is created.
				this.view.renderViewComposition()
					.then(this._resetModelFromPersistedValues.bind(this))
					.then(this.removeLoading.bind(this));
			} else {
				this._resetModelFromPersistedValues()
					.then(this.removeLoading.bind(this));
			}
		},

		_getColumnData: function(searchStr){
			return this.queryHelper.getColumnData(this.scope, searchStr);
		},

		_getRootMembers: function(){
			return this.queryHelper.getRootMembers(this.scope);
		},

		_getChildren: function(parentId, offset){
			return this.queryHelper.getChildren(this.scope, parentId,  ROW_LIMIT, offset);
		},

		_getAscendants: function(childIds){
			if( childIds && childIds.length ){
				return this.queryHelper.getAscendants(this.scope, childIds);
			}else{
				// save the query if we don't need anything.
				return Promise.resolve([]);
			}
		},

		/**
		 * Commence the render sequence of the Member filter view.
		 *
		 * @param {object} [options] - options
		 * @param {string} [options.searchIcon] - search icon to use; if none provided, no icon will be displayed
		 *
		 * @return {Promise} Resolves when render sequence is complete.
		 */
		render: function () {
			this.setLoading();
			return this.view.renderViewComposition()
				.then(this._resetModelFromPersistedValues.bind(this))
				.then(this.removeLoading.bind(this));
		},

		/**
		 * This function is called by processContextChange if page context change impacts current filter.
		 * @return {Promise}
		 */
		_updateFilterDataOnPageContextChange: function(){
			return this._resetModelFromPersistedValues();

		},
		_resetModelFromPersistedValues: function(){
			var initialSelection = this._getInitialSelection();
			var name = this._getDialogCaption();
			var inverted = this._getSelectionIsInverted();

			return Promise.all([
				this._getRootMembers(),
				this._getAscendants(initialSelection)
			]).then(function (results) {
				if (this.model && this.view) {
					var roots = results[0];
					var ascendants = results[1];

					this.model.setName(name);
					this.model.reset({ roots : roots, ascendants : ascendants, initialSelection: initialSelection, inverted: inverted });
					this.view.resetView();
				}
			}.bind(this))
				.catch( function(e) {
					this.processError(e);
				}.bind(this));
		},


		/* returns the currently saved selection from the page context item
		 * or empty if there is none.
		 */
		_getInitialSelection: function(){
			var pageContextItem = this.getCurrentPageContextItem();
			return pageContextItem ? pageContextItem.getValueIds() : [];
		},

		/* returns  the dialog caption from the page context or the item context name*/
		_getDialogCaption: function(){
			var pageContextItem = this.getCurrentPageContextItem();
			return pageContextItem ? pageContextItem.getItemName() : this.itemContext.itemName;
		},
		/* returns the inverted state of the persisted filter item */
		_getSelectionIsInverted: function(){
			var pageContextItem = this.getCurrentPageContextItem();
			return pageContextItem ? pageContextItem.getExclude() : false;
		},

		refreshDataAndView: function(/*payload*/) {
			//nothing to do since Shaping Control is unable to display hieararchy data sources
			return Promise.resolve();
		}
	});
	return HierarchicalFilterController;
});
